import React, { useState, useEffect } from 'react';
import { useOnboarding } from '../contexts/OnboardingContext';
import EditProfileModal from './EditProfileModal';
import { secureStorage } from '../utils/security';
import './ProfilePage.css';

const ProfilePage = ({ onClose }) => {
  const { profile, walletAddress, updateProfile } = useOnboarding();
  const [showEditModal, setShowEditModal] = useState(false);
  const [userProfile, setUserProfile] = useState({
    displayName: '',
    username: '',
    bio: '',
    location: '',
    website: '',
    bannerImage: '',
    profileImage: '',
    joinDate: '',
    followingCount: 0,
    followersCount: 0,
    postsCount: 0
  });

  useEffect(() => {
    loadUserProfile();
  }, [profile, walletAddress]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadUserProfile = async () => {
    try {
      // Load profile from onboarding context or storage
      const savedProfile = await secureStorage.getItem(`profile_${walletAddress}`);
      
      if (savedProfile) {
        const parsedProfile = JSON.parse(savedProfile);
        setUserProfile({
          displayName: parsedProfile.displayName || profile?.displayName || 'Anonymous User',
          username: parsedProfile.username || profile?.username || `@${walletAddress?.slice(0, 8)}...`,
          bio: parsedProfile.bio || 'Most people overestimate what they can do awake in a year, and underestimate what they can dream in a decade. 🏔️🎁 Dream bigger. Snore louder. ZNOOZINU 🏔️🔊',
          location: parsedProfile.location || 'San Francisco',
          website: parsedProfile.website || '',
          bannerImage: parsedProfile.bannerImage || '',
          profileImage: parsedProfile.profileImage || '',
          joinDate: parsedProfile.joinDate || 'May 2025',
          followingCount: parsedProfile.followingCount || 0,
          followersCount: parsedProfile.followersCount || 0,
          postsCount: parsedProfile.postsCount || 38
        });
      } else {
        // Set default profile data
        setUserProfile({
          displayName: profile?.displayName || 'ZNOOZINU',
          username: profile?.username || '@znoozinu',
          bio: 'Most people overestimate what they can do awake in a year, and underestimate what they can dream in a decade. 🏔️🎁 Dream bigger. Snore louder. ZNOOZINU 🏔️🔊',
          location: 'San Francisco',
          website: '',
          bannerImage: '',
          profileImage: '',
          joinDate: 'May 2025',
          followingCount: 0,
          followersCount: 0,
          postsCount: 38
        });
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  };

  const handleEditProfile = () => {
    setShowEditModal(true);
  };

  const handleSaveProfile = async (updatedProfile) => {
    try {
      // Update profile in onboarding context (this will sync with MainContent)
      await updateProfile({
        displayName: updatedProfile.displayName,
        username: updatedProfile.username,
        bio: updatedProfile.bio,
        location: updatedProfile.location,
        website: updatedProfile.website,
        bannerImage: updatedProfile.bannerImage,
        profileImage: updatedProfile.profileImage
      });
      
      // Also save to local storage for persistence
      await secureStorage.setItem(`profile_${walletAddress}`, JSON.stringify(updatedProfile));
      setUserProfile(updatedProfile);
      setShowEditModal(false);
      console.log('Profile updated successfully and synced with main app');
    } catch (error) {
      console.error('Error saving profile:', error);
    }
  };

  const handleBannerClick = () => {
    if (showEditModal) return;
    // Open file picker for banner image
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const newProfile = { ...userProfile, bannerImage: event.target.result };
          handleSaveProfile(newProfile);
        };
        reader.readAsDataURL(file);
      }
    };
    input.click();
  };

  return (
    <div className="profile-page-overlay">
      <div className="profile-page-container">
        {/* Header */}
        <div className="profile-header">
          <button className="back-button" onClick={onClose}>
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
              <path d="M7.414 13l5.043 5.04-1.414 1.42L3.586 12l7.457-7.46 1.414 1.42L7.414 11H21v2H7.414z"/>
            </svg>
          </button>
          <div className="profile-header-info">
            <h2>{userProfile.displayName}</h2>
            <span className="posts-count">{userProfile.postsCount} posts</span>
          </div>
          <button className="search-button">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
              <path d="M10.25 3.75c-3.59 0-6.5 2.91-6.5 6.5s2.91 6.5 6.5 6.5c1.795 0 3.419-.726 4.596-1.904 1.178-1.177 1.904-2.801 1.904-4.596 0-3.59-2.91-6.5-6.5-6.5zm-8.5 6.5c0-4.694 3.806-8.5 8.5-8.5s8.5 3.806 8.5 8.5c0 1.986-.682 3.815-1.824 5.262l4.781 4.781-1.414 1.414-4.781-4.781c-1.447 1.142-3.276 1.824-5.262 1.824-4.694 0-8.5-3.806-8.5-8.5z"/>
            </svg>
          </button>
        </div>

        {/* Banner Section */}
        <div 
          className="profile-banner" 
          onClick={handleBannerClick}
          style={{
            backgroundImage: userProfile.bannerImage ? `url(${userProfile.bannerImage})` : 'none',
            backgroundColor: userProfile.bannerImage ? 'transparent' : '#536471'
          }}
        >
          {!userProfile.bannerImage && (
            <div className="banner-placeholder">
              <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                <path d="M3 5.5C3 4.119 4.119 3 5.5 3h13C19.881 3 21 4.119 21 5.5v13c0 1.381-1.119 2.5-2.5 2.5h-13C4.119 21 3 19.881 3 18.5v-13zM5.5 5c-.276 0-.5.224-.5.5v9.086l3-3 3 3 5-5 3 3V5.5c0-.276-.224-.5-.5-.5h-13zM19 15.414l-3-3-5 5-3-3-3 3V18.5c0 .276.224.5.5.5h13c.276 0 .5-.224.5-.5v-3.086zM9.75 7C8.784 7 8 7.784 8 8.75s.784 1.75 1.75 1.75 1.75-.784 1.75-1.75S10.716 7 9.75 7z"/>
              </svg>
            </div>
          )}
        </div>

        {/* Profile Info Section */}
        <div className="profile-info-section">
          <div className="profile-avatar-container">
            <div className="profile-avatar">
              {userProfile.profileImage ? (
                <img src={userProfile.profileImage} alt="Profile" />
              ) : (
                <div className="avatar-placeholder">
                  <svg viewBox="0 0 24 24" width="40" height="40" fill="currentColor">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                  </svg>
                </div>
              )}
            </div>
            <button className="edit-profile-btn" onClick={handleEditProfile}>
              Edit profile
            </button>
          </div>

          <div className="profile-details">
            <h1 className="profile-name">
              {userProfile.displayName}
              <svg className="verified-badge" viewBox="0 0 24 24" width="20" height="20" fill="#1d9bf0">
                <path d="M22.25 12c0-1.43-.88-2.67-2.19-3.34.46-1.39.2-2.9-.81-3.91s-2.52-1.27-3.91-.81c-.66-1.31-1.91-2.19-3.34-2.19s-2.67.88-3.33 2.19c-1.4-.46-2.91-.2-3.92.81s-1.26 2.52-.8 3.91c-1.31.67-2.2 1.91-2.2 3.34s.89 2.67 2.2 3.34c-.46 1.39-.21 2.9.8 3.91s2.52 1.27 3.91.81c.67 1.31 1.91 2.19 3.34 2.19s2.68-.88 3.34-2.19c1.39.46 2.9.2 3.91-.81s1.27-2.52.81-3.91c1.31-.67 2.19-1.91 2.19-3.34zm-11.71 4.2L6.8 12.46l1.41-1.42 2.26 2.26 4.8-5.23 1.47 1.36-6.2 6.77z"/>
              </svg>
            </h1>
            <p className="profile-username">{userProfile.username}</p>
            
            <div className="profile-bio">
              <p>{userProfile.bio}</p>
            </div>

            <div className="profile-metadata">
              {userProfile.location && (
                <div className="metadata-item">
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                    <path d="M12 7c-1.93 0-3.5 1.57-3.5 3.5S10.07 14 12 14s3.5-1.57 3.5-3.5S13.93 7 12 7zm0 5c-.827 0-1.5-.673-1.5-1.5S11.173 9 12 9s1.5.673 1.5 1.5S12.827 12 12 12zm0-10c-4.687 0-8.5 3.813-8.5 8.5 0 5.967 7.621 11.116 7.945 11.332l.555.37.555-.37C12.879 21.616 20.5 16.467 20.5 10.5 20.5 5.813 16.687 2 12 2zm0 17.77c-1.665-1.241-6.5-5.196-6.5-9.27C5.5 6.916 8.416 4 12 4s6.5 2.916 6.5 6.5c0 4.073-4.835 8.028-6.5 9.27z"/>
                  </svg>
                  <span>{userProfile.location}</span>
                </div>
              )}
              <div className="metadata-item">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                  <path d="M7 4V2c0-.55-.45-1-1-1s-1 .45-1 1v2H3c-.55 0-1 .45-1 1v14c0 .55.45 1 1 1h18c.55 0 1-.45 1-1V5c0-.55-.45-1-1-1h-2V2c0-.55-.45-1-1-1s-1 .45-1 1v2H7zM3 8h18v11H3V8zm2-2h14V5H5v1z"/>
                </svg>
                <span>Joined {userProfile.joinDate}</span>
              </div>
            </div>

            <div className="follow-stats">
              <div className="follow-stat">
                <span className="stat-number">{userProfile.followingCount}</span>
                <span className="stat-label">Following</span>
              </div>
              <div className="follow-stat">
                <span className="stat-number">{userProfile.followersCount}</span>
                <span className="stat-label">Followers</span>
              </div>
            </div>
          </div>
        </div>

        {/* Posts/Timeline would go here */}
        <div className="profile-timeline">
          <div className="timeline-tabs">
            <button className="timeline-tab active">Posts</button>
            <button className="timeline-tab">Replies</button>
            <button className="timeline-tab">Media</button>
            <button className="timeline-tab">Likes</button>
          </div>
          <div className="timeline-content">
            <div className="no-posts">
              <p>Your posts will appear here when you start sharing your thoughts with the cosmos.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Profile Modal */}
      {showEditModal && (
        <EditProfileModal
          profile={userProfile}
          onSave={handleSaveProfile}
          onClose={() => setShowEditModal(false)}
        />
      )}
    </div>
  );
};

export default ProfilePage;
